import type { TranslatorUnbranded } from "../../assessments/presentation.js";
import type {
  StructuralControl,
  StructuralGroup,
  StructuralItemView,
  TextGroup,
  TextItemView,
  TextShown,
} from "./decorator.js";
import { isCommentPlacement, type FormText } from "./plainForm.js";

/**
 * A bound for every field of a view, each saying why a decorated view goes
 * beyond what it may be, or `undefined` where it does not: judged against the
 * plain form's (`plain`) for what the instrument allows, and against the view
 * the decorator was given (`given`) for what its kind may change. Keyed by
 * every field, optional ones included, so a field added to a view fails to
 * compile until someone decides what may be done to it.
 */
type Bounds<View> = {
  readonly [Field in keyof View]-?: (views: {
    plain: View;
    given: View;
    decorated: View;
  }) => string | undefined;
};

/**
 * The plain controls each control other than a choice may stand in for.
 *
 * A decorator may only narrow its item, so a control may collect only values
 * the plain one could: a date is a string, and never one of a closed set.
 */
const standsInFor: Readonly<
  Record<
    Exclude<StructuralControl["kind"], "choice">,
    readonly StructuralControl["kind"][]
  >
> = {
  text: ["text"],
  date: ["text"],
};

const sameValues = (
  left: readonly unknown[],
  right: readonly unknown[],
): boolean =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const itemBounds: Bounds<StructuralItemView> = {
  at: ({ plain, decorated }) =>
    plain.at.group === decorated.at.group && plain.at.item === decorated.at.item
      ? undefined
      : "a decorator cannot move an item",
  responseSet: ({ plain, decorated }) =>
    plain.responseSet === decorated.responseSet
      ? undefined
      : "a decorator cannot change the response set an item draws on",
  control: ({
    plain: { control: plain },
    decorated: { control: decorated },
  }) => {
    if (decorated.kind !== "choice") {
      return standsInFor[decorated.kind].includes(plain.kind)
        ? undefined
        : `a ${decorated.kind} control cannot stand in for a ${plain.kind} control`;
    }

    if (plain.kind !== "choice") {
      return `a choice control cannot stand in for a ${plain.kind} control`;
    }

    return sameValues(plain.values, decorated.values)
      ? undefined
      : "a choice must offer the instrument's answers, in its order";
  },
  // Only a narrowing: the form may insist on what the instrument lets a
  // document omit, but never release what it requires, nor claim its own
  // insistence is the instrument's.
  required: ({ plain, decorated }) => {
    if (decorated.required === plain.required) {
      return undefined;
    }

    if (plain.required === "by_instrument") {
      return "the instrument requires it, so a decorator cannot change who requires it";
    }

    return decorated.required === "by_form"
      ? undefined
      : "a decorator may require it by the form, never by the instrument";
  },
  // Hiding what the version requires would refuse every document that should
  // hold it.
  asked: ({ plain, decorated }) =>
    decorated.asked === "always" || plain.required !== "by_instrument"
      ? undefined
      : "only an optional item may be hidden",
  // The collector's own, never the instrument's, so any change is its choice.
  checks: () => undefined,
  acceptsComment: ({ plain, decorated }) =>
    plain.acceptsComment === decorated.acceptsComment
      ? undefined
      : "a decorator cannot change whether an item takes a comment",
  // The instrument never requires a comment, so requiring one only narrows;
  // but it needs a comment box to be given in.
  commentRequired: ({ decorated }) =>
    decorated.commentRequired === "never" || decorated.acceptsComment
      ? undefined
      : "a comment cannot be required of an item that takes none",
};

const groupBounds: Bounds<StructuralGroup> = {
  at: ({ plain, decorated }) =>
    plain.at.group === decorated.at.group
      ? undefined
      : "a decorator cannot move a group",
  required: ({ plain, decorated }) =>
    plain.required === decorated.required
      ? undefined
      : "a decorator cannot change whether the instrument requires a group",
  asked: ({ plain, decorated }) =>
    decorated.asked === "always" || plain.required !== "by_instrument"
      ? undefined
      : "only an optional group may be hidden",
};

const beyond =
  <View>(bounds: Bounds<View>) =>
  (views: { plain: View; given: View; decorated: View }): string[] =>
    Object.values<Bounds<View>[keyof View]>(bounds).flatMap(
      (bound) => bound(views) ?? [],
    );

/**
 * Everything a structural decorator's result for an item does beyond what the
 * instrument allows, judged against its plain form: whatever a decorator
 * returns, a document the form accepts must be one the instrument accepts.
 */
export const itemBeyondTheInstrument = beyond(itemBounds);

/** As `itemBeyondTheInstrument`, for a group. */
export const groupBeyondTheInstrument = beyond(groupBounds);

/** Refuses a text decorator changing what it may only read. */
const readOnly = (what: string, unchanged: boolean): string | undefined =>
  unchanged ? undefined : `a text decorator cannot change ${what}`;

const sameTranslator = (
  left: TranslatorUnbranded,
  right: TranslatorUnbranded,
): boolean =>
  left.service === right.service &&
  left.version === right.version &&
  left.translatedAt === right.translatedAt;

/** Whether two origins are the same, down to what a translation was made from and by. */
const sameOrigin = (
  left: FormText["origin"],
  right: FormText["origin"],
): boolean => {
  if (
    left.kind === "machine-translated" &&
    right.kind === "machine-translated"
  ) {
    return sameTranslator(left.by, right.by) && sameText(left.from, right.from);
  }

  return left.kind === right.kind;
};

const sameText = (left: FormText, right: FormText): boolean =>
  left.text === right.text &&
  sameOrigin(left.origin, right.origin) &&
  ("language" in left ? left.language : undefined) ===
    ("language" in right ? right.language : undefined);

/**
 * Why a text is not one a decorator may show in place of the plain form's: a
 * text a decorator changes is its collector's words, so it must say so, never
 * passing as the instrument's, its key, or a translation.
 */
const wordsBeyond = (
  what: string,
  plain: FormText | undefined,
  decorated: FormText | undefined,
): string | undefined => {
  if (decorated === undefined) {
    return plain === undefined
      ? undefined
      : `a decorator cannot remove the ${what}`;
  }

  return (plain !== undefined && sameText(plain, decorated)) ||
    decorated.origin.kind === "collector"
    ? undefined
    : `the ${what} a decorator changes must be the collector's`;
};

const shownBounds = <View extends TextShown>(): Pick<
  Bounds<View>,
  "title" | "description"
> => ({
  title: ({ plain, decorated }) =>
    wordsBeyond("title", plain.title, decorated.title),
  description: ({ plain, decorated }) =>
    wordsBeyond("description", plain.description, decorated.description),
});

const textItemBounds: Bounds<TextItemView> = {
  at: ({ given, decorated }) =>
    readOnly(
      "where an item is",
      given.at.group === decorated.at.group &&
        given.at.item === decorated.at.item,
    ),
  responseSet: ({ given, decorated }) =>
    readOnly(
      "the response set an item draws on",
      given.responseSet === decorated.responseSet,
    ),
  control: ({ given: { control: given }, decorated: { control: decorated } }) =>
    readOnly(
      "a control",
      given.kind === "choice" && decorated.kind === "choice"
        ? sameValues(given.values, decorated.values)
        : given.kind === decorated.kind,
    ),
  required: ({ given, decorated }) =>
    readOnly(
      "whether an item is required",
      given.required === decorated.required,
    ),
  asked: ({ given, decorated }) =>
    readOnly("whether an item is asked", given.asked === decorated.asked),
  checks: ({ given, decorated }) =>
    readOnly("an item's checks", sameValues(given.checks, decorated.checks)),
  commentRequired: ({ given, decorated }) =>
    readOnly(
      "whether a comment is required",
      given.commentRequired === decorated.commentRequired,
    ),
  language: ({ given, decorated }) =>
    readOnly("the form's language", given.language === decorated.language),
  fallbackLanguage: ({ given, decorated }) =>
    readOnly(
      "the fallback language",
      given.fallbackLanguage === decorated.fallbackLanguage,
    ),
  ...shownBounds<TextItemView>(),
  // Words only: which answers are offered, and in what order, is structure.
  answers: ({ plain, decorated }) => {
    if (
      !sameValues(
        plain.answers.map(({ value }) => value),
        decorated.answers.map(({ value }) => value),
      )
    ) {
      return "a text decorator cannot change the answers offered";
    }

    const beyondIn = decorated.answers.flatMap((reworded) => {
      const answer = plain.answers.find(
        ({ value }) => value === reworded.value,
      );
      const what = `answer ${JSON.stringify(reworded.value)}`;

      return [
        wordsBeyond(`title of ${what}`, answer?.title, reworded.title),
        wordsBeyond(
          `description of ${what}`,
          answer?.description,
          reworded.description,
        ),
      ].flatMap((why) => why ?? []);
    });

    return beyondIn.length === 0 ? undefined : beyondIn.join("; ");
  },
  acceptsComment: ({ given, decorated }) =>
    readOnly(
      "whether an item takes a comment",
      given.acceptsComment === decorated.acceptsComment,
    ),
  banners: ({ decorated: { banners, acceptsComment } }) => {
    const beyondIn = banners.flatMap(({ placement, text }) => [
      ...(text === undefined
        ? [
            `its ${placement} banner gives no words in the form's language or the fallback language`,
          ]
        : []),
      ...(text !== undefined && text.origin.kind !== "collector"
        ? [`its ${placement} banner's words must be the collector's`]
        : []),
      ...(isCommentPlacement(placement) && !acceptsComment
        ? [
            `its ${placement} banner needs a comment box, which it does not take`,
          ]
        : []),
    ]);

    return beyondIn.length === 0 ? undefined : beyondIn.join("; ");
  },
};

const textGroupBounds: Bounds<TextGroup> = {
  at: ({ given, decorated }) =>
    readOnly("where a group is", given.at.group === decorated.at.group),
  required: ({ given, decorated }) =>
    readOnly(
      "whether a group is required",
      given.required === decorated.required,
    ),
  asked: ({ given, decorated }) =>
    readOnly("whether a group is asked", given.asked === decorated.asked),
  language: ({ given, decorated }) =>
    readOnly("the form's language", given.language === decorated.language),
  fallbackLanguage: ({ given, decorated }) =>
    readOnly(
      "the fallback language",
      given.fallbackLanguage === decorated.fallbackLanguage,
    ),
  ...shownBounds<TextGroup>(),
};

/**
 * Everything a text decorator's result for an item does beyond what it may:
 * any change to its structure, which it may only read, any text it changes
 * that does not record itself as the collector's, and a banner with no
 * words, or placed by a comment box the item does not take.
 */
export const textItemBeyond = beyond(textItemBounds);

/** As `textItemBeyond`, for a group. */
export const textGroupBeyond = beyond(textGroupBounds);
